import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import {
  Button,
  Field,
  FieldLabel,
  Input,
  DatePicker,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from 'prosci-ui';
import { productService } from '@/services/product-service';
import { affiliateService } from '@/services/affiliate-service';
import { organizationService } from '@/services/organization-service';
import type { ReportFilters, FilterConfig, AdminLevel } from '@/types/reports';
import { parseDateLocal } from '@/lib/date-utils';

interface ReportFiltersProps {
  filters: ReportFilters;
  filterConfig: FilterConfig;
  adminLevel?: AdminLevel;
  isAffiliate?: boolean;
  onFilterChange: (key: keyof ReportFilters, value: string | undefined) => void;
  onDateChange: (key: 'from_date' | 'to_date', date: Date | undefined) => void;
  onClear: () => void;
  hasFilters: boolean;
}

function DebouncedTextInput({
  value,
  fieldKey,
  onFilterChange,
  placeholder,
  id,
}: {
  value: string;
  fieldKey: string;
  onFilterChange: (key: string, value: string | undefined) => void;
  placeholder?: string;
  id: string;
}) {
  const [local, setLocal] = useState(value);
  const isFirstRun = useRef(true);

  // Sync when the external value resets (e.g. clear filters)
  useEffect(() => { setLocal(value); }, [value]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    const timer = setTimeout(() => onFilterChange(fieldKey, local || undefined), 400);
    return () => clearTimeout(timer);
  }, [local, fieldKey, onFilterChange]);

  return (
    <Input
      type="text"
      id={id}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      placeholder={placeholder}
      className="w-full"
    />
  );
}

/**
 * Reusable filter bar component for reports
 */
export function ReportFiltersBar({
  filters,
  filterConfig,
  adminLevel,
  isAffiliate,
  onFilterChange,
  onDateChange,
  onClear,
  hasFilters,
}: ReportFiltersProps) {
  const showFilters = !!adminLevel && ['super', 'admin', 'affiliate_admin', 'data'].includes(adminLevel);

  // Fetch products for product select filters
  const { data: productsData } = useQuery<{
    data: Array<{ id: string; attributes?: { name?: string; product_type?: string }; name?: string }>;
  }>({
    queryKey: ['products-filter'],
    queryFn: () =>
      productService.listProducts({ active: true }) as Promise<{
        data: Array<{ id: string; attributes?: { name?: string; product_type?: string }; name?: string }>;
      }>,
    enabled: filterConfig.fields.some(
      (f) => f.type === 'product-select' || f.type === 'elearning-product-select'
    ),
  });

  // Filter for eLearning products only
  const elearningProducts = productsData?.data?.filter(
    (p) =>
      p.attributes?.name?.toLowerCase().includes('elearning') ||
      p.attributes?.product_type === 'elearning'
  );

  // Fetch affiliates for affiliate select
  const { data: affiliatesData } = useQuery<{
    data: Array<{ id: string; attributes?: { name: string } }>;
  }>({
    queryKey: ['affiliates-filter'],
    queryFn: () =>
      affiliateService.listAffiliates() as Promise<{
        data: Array<{ id: string; attributes?: { name: string } }>;
      }>,
    enabled:
      showFilters &&
      filterConfig.fields.some((f) => f.type === 'affiliate-select'),
  });

  // Fetch organizations for organization select
  const { data: organizationsData } = useQuery<{
    data: Array<{ id: string; attributes?: { name: string } }>;
  }>({
    queryKey: ['organizations-filter'],
    queryFn: () =>
      organizationService.listOrganizations({
        active_licenses_only: 'true' as unknown as boolean,
      }) as Promise<{ data: Array<{ id: string; attributes?: { name: string } }> }>,
    enabled:
      showFilters &&
      filterConfig.fields.some((f) => f.type === 'organization-select'),
  });

  // Filter fields based on admin level
  const visibleFields = filterConfig.fields.filter((field) => {
    if (!field.showFor) return true;
    if (!adminLevel) return false;
    return field.showFor.includes(adminLevel);
  });

  const renderFilterField = (field: typeof visibleFields[0]) => {
    const fieldKey = field.key;

    switch (field.type) {
      case 'text':
        return (
          <Field key={fieldKey}>
            <FieldLabel htmlFor={fieldKey}>{field.label}</FieldLabel>
            <DebouncedTextInput
              id={fieldKey}
              fieldKey={fieldKey}
              value={(filters[fieldKey] as string) || ''}
              onFilterChange={onFilterChange}
              placeholder={field.placeholder}
            />
          </Field>
        );

      case 'date':
        return (
          <Field key={fieldKey}>
            <FieldLabel htmlFor={fieldKey}>{field.label}</FieldLabel>
            <DatePicker
              id={fieldKey}
              date={
                filters[fieldKey]
                  ? parseDateLocal(filters[fieldKey] as string)
                  : undefined
              }
              onDateChange={(date: Date | undefined) =>
                onDateChange(fieldKey as 'from_date' | 'to_date', date)
              }
              placeholder={field.placeholder || 'Select date'}
              captionLayout="dropdown"
              fromYear={2020}
              toYear={new Date().getFullYear()}
              className="h-9 normal-case bg-transparent hover:bg-transparent text-base px-3"
            />
          </Field>
        );

      case 'select':
        return (
          <Field key={fieldKey}>
            <FieldLabel htmlFor={fieldKey}>{field.label}</FieldLabel>
            <Select
              value={(filters[fieldKey] as string) || ''}
              onValueChange={(value) => onFilterChange(fieldKey, value || undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={field.placeholder || 'Select...'} />
              </SelectTrigger>
              <SelectContent position="popper">
                {field.options?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        );

      case 'product-select':
        return (
          <Field key={fieldKey}>
            <FieldLabel htmlFor={fieldKey}>{field.label}</FieldLabel>
            <Select
              value={(filters[fieldKey] as string) || ''}
              onValueChange={(value) => onFilterChange(fieldKey, value || undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={field.placeholder || 'All Products'} />
              </SelectTrigger>
              <SelectContent position="popper">
                {productsData?.data?.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.attributes?.name || product.name || ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        );

      case 'elearning-product-select':
        return (
          <Field key={fieldKey}>
            <FieldLabel htmlFor={fieldKey}>{field.label}</FieldLabel>
            <Select
              value={(filters[fieldKey] as string) || ''}
              onValueChange={(value) => onFilterChange(fieldKey, value || undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={field.placeholder || 'All eLearning Courses'} />
              </SelectTrigger>
              <SelectContent position="popper">
                {elearningProducts?.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.attributes?.name || product.name || ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        );

      case 'affiliate-select':
        // Hide affiliate filter for affiliate users (their filter is locked)
        if (!showFilters || isAffiliate) return null;
        return (
          <Field key={fieldKey}>
            <FieldLabel htmlFor={fieldKey}>{field.label}</FieldLabel>
            <Select
              value={(filters[fieldKey] as string) || ''}
              onValueChange={(value) => onFilterChange(fieldKey, value || undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={field.placeholder || 'All Affiliates'} />
              </SelectTrigger>
              <SelectContent position="popper">
                {affiliatesData?.data?.map((affiliate) => (
                  <SelectItem key={affiliate.id} value={affiliate.id}>
                    {affiliate.attributes?.name || ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        );

      case 'organization-select':
        if (!showFilters) return null;
        return (
          <Field key={fieldKey}>
            <FieldLabel htmlFor={fieldKey}>{field.label}</FieldLabel>
            <Select
              value={(filters[fieldKey] as string) || ''}
              onValueChange={(value) => onFilterChange(fieldKey, value || undefined)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={field.placeholder || 'All License Holders'} />
              </SelectTrigger>
              <SelectContent position="popper">
                {organizationsData?.data?.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.attributes?.name || ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        );

      default:
        return null;
    }
  };

  // Calculate grid columns based on number of visible fields
  const gridCols =
    visibleFields.length <= 2
      ? 'lg:grid-cols-2'
      : visibleFields.length === 3
        ? 'lg:grid-cols-3'
        : 'lg:grid-cols-4';

  return (
    <div className="bg-white border-b border-accent-sand">
      <div className="max-w-[1160px] px-6 py-4 mx-auto">
        <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} gap-4`}>
          {visibleFields.map(renderFilterField)}
        </div>

        {hasFilters && (
          <div className="flex items-center justify-end mt-4">
            <Button type="button" variant="ghost" onClick={onClear}>
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
